import type { Copy, Movie } from './types';

/**
 * Where the discs are.
 *
 * A collection outgrows one shelf: some of it is in the living room, some at
 * a parent's place, some in a box in the cellar. Knowing which is which is
 * what lets you answer "what can we actually watch tonight".
 *
 * This module deliberately knows nothing about the rest of the settings — it
 * takes the smallest shape that can answer the question, so the search layer
 * can use it without importing the settings layer that imports the search
 * layer back.
 */

export interface ShelfLocation {
  id: string;
  /** "Living room", "Mum's place", "The box in the cellar". */
  name: string;
}

/** Just the parts of the settings that say where something is. */
export interface LocationBook {
  locations: ShelfLocation[];
  /**
   * Where a copy is when it does not say — which is every copy added before
   * locations existed. Null only while there are no locations at all.
   */
  defaultLocationId: string | null;
}

export const EMPTY_LOCATIONS: LocationBook = { locations: [], defaultLocationId: null };

export const MAX_LOCATION_NAME = 60;

/**
 * Where this disc actually is.
 *
 * A copy that says nothing, or points at a location that has since been
 * deleted, is at the default. That is what keeps every disc added before any
 * of this existed out of a nowhere bucket, and it means deleting a location
 * never orphans the copies that pointed at it.
 */
export function effectiveLocationId(copy: Copy, book: LocationBook): string | null {
  const claimed = copy.locationId ?? null;
  if (claimed !== null && book.locations.some((entry) => entry.id === claimed)) return claimed;
  return book.defaultLocationId;
}

export function locationName(id: string | null, book: LocationBook): string | null {
  if (id === null) return null;
  return book.locations.find((entry) => entry.id === id)?.name ?? null;
}

/** Every location this film is at — usually one, more when copies are split. */
export function movieLocationIds(movie: Movie, book: LocationBook): string[] {
  const ids = new Set<string>();
  for (const copy of movie.copies) {
    const id = effectiveLocationId(copy, book);
    if (id !== null) ids.add(id);
  }
  return [...ids];
}

/**
 * Is any copy of this film at one of these locations?
 *
 * Any, not all: if you own the 4K at home and lent the Blu-ray to your
 * brother, the film is still here.
 */
export function movieIsAtLocation(
  movie: Movie,
  locationIds: readonly string[],
  book: LocationBook,
): boolean {
  if (locationIds.length === 0) return true;
  return movie.copies.some((copy) => {
    const id = effectiveLocationId(copy, book);
    return id !== null && locationIds.includes(id);
  });
}

export function parseLocations(input: unknown): ShelfLocation[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const locations: ShelfLocation[] = [];

  for (const entry of input) {
    if (typeof entry !== 'object' || entry === null) continue;
    const raw = entry as Record<string, unknown>;
    const id = typeof raw.id === 'string' ? raw.id : null;
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    if (id === null || name === '' || seen.has(id)) continue;
    seen.add(id);
    locations.push({ id, name: name.slice(0, MAX_LOCATION_NAME) });
  }

  return locations;
}
